// Los cambios del reloj de semanas, dichos para una persona (06-10).
//
// El reloj (backend/core/reloj.py) apunta UN cambio por campo. Desde que lee sus reglas del
// catálogo, una sola regla mueve varios campos a la vez (la semana, el estado del mensual,
// sus marcas internas y los efectos que pide) y todos llevan el mismo motivo: pintados tal
// cual, «R9 · Se envía el reporte mensual» salía tres veces.
//
// Aquí se juntan los cambios que comparten instante, regla y cliente en UNA línea: el título
// de la regla una vez y, al lado, lo que cambió. Los cambios sin `regla` (el camino de antes
// del catálogo, fijar a mano) no se tocan: salen uno a uno, como siempre.
//
// Lógica pura: sin React y sin red. La usan SimuladorPasoAPaso y AdminRelojPage.

import { aFecha, diaDeCalendario, diaEnEspana, horaEnEspana } from './horaEspana';

// Marcas que el reloj guarda para sí: no dicen nada a quien mira la pantalla.
// (`semana0_desde`: desde qué día está en la semana 0; salía «Semana0 desde: ... → nada» al fijarla a mano.)
const INTERNOS = ['semana_desde', 'mensual_enviado', 'semana0_desde', 'repeticiones', 'tipo_ciclo'];
// Dentro de una regla tampoco se enseñan las banderas del camino de antes.
// (`fuera_de_hora`: el apunte de que una regla se aplicó fuera de su hora, en la prueba de una regla.)
// (`rutina_del_ciclo`: qué reglas de la rutina han saltado ya en el ciclo; lo que se ve es su tarea o su entrega.)
const INTERNOS_DE_UNA_REGLA = [...INTERNOS, 'hasta', 'listo_para_4', 'ciclo_reinicia', 'semana0_desde', 'fuera_de_hora',
    'rutina_del_ciclo'];

const ESTADOS = {
    ENVIADO: 'Enviado',
    'ENVIADO RECORDATORIO DOMINGO': 'Recordatorio del domingo',
    'ENVIADO RECORDATORIO LUNES': 'Recordatorio del lunes',
    'NO ENVIADO': 'No enviado',
    RELLENADO: 'Rellenado',
    CERRADO: 'Cerrado',
};

const CORREOS = {
    reporte_quincenal: 'Se envía el correo del reporte quincenal',
    reporte_mensual: 'Se envía el correo del reporte mensual',
    recordatorio_reporte_mensual: 'Se envía el recordatorio del reporte mensual',
    recordatorio_reporte_mensual_domingo: 'Se envía el recordatorio del domingo',
    recordatorio_reporte_mensual_lunes: 'Se envía el recordatorio del lunes',
    reporte_mensual_no_enviado: 'Se envía el correo de reporte mensual no enviado',
};

const TAREAS = {
    preparar_macros_y_rutina: 'preparar sus macros y su rutina',
    responder_quincenal: 'responder el quincenal',
    revisar_reporte_y_ajustar: 'revisar el reporte y ajustar',
    pasar_a_la_semana_siguiente_a_mano: 'pasarlo a la semana siguiente a mano',
    preparar_rutina_nueva: 'preparar la rutina nueva',
    contactar_al_cliente: 'contactar con el cliente',
};

// Efectos que no son ni un correo ni una tarea. Los que no estén aquí salen legibles igual.
const OTROS_EFECTOS = {
    'empieza_semana_1: lunes_siguiente': 'Su semana 1 empieza el lunes siguiente',
    'empieza_semana_1: lunes_siguiente_a_la_entrega': 'Su semana 1 empieza el lunes siguiente a la entrega',
    'entregar: rutina_nueva': 'Recibe su rutina nueva',
    'entregar: macros_al_momento': 'Recibe sus macros al momento',
};

const mayuscula = (texto) => (texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : '');

/** Un nombre interno, legible: sin guiones bajos. `revisar_algo` -> «revisar algo». */
export const legible = (valor) => String(valor ?? '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();

const vacio = (v) => v === null || v === undefined || v === '';

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre',
    'noviembre', 'diciembre'];

/**
 * Una fecha del reloj dicha como en el resto de la app: «lunes 14 de diciembre a las 23:00», o
 * «lunes 16 de noviembre» si es un día sin hora. Un instante (con su hora) se pasa SIEMPRE a
 * hora de España con `horaEspana.js`: el reloj los guarda ya así («...T23:00:00+01:00»), pero
 * por aquí pasan también fechas de la ficha guardadas en UTC («...T23:30:00+00:00», que en
 * España es el día siguiente). Un día sin hora se lee del texto, sin zona.
 * Devuelve null si el texto no es una fecha.
 */
export const fechaDelReloj = (texto) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})(T\d{2}:\d{2})?/.exec(typeof texto === 'string' ? texto : '');
    if (!m) return null;
    let dia = `${m[1]}-${m[2]}-${m[3]}`;
    let hora = '';
    // Un día guardado a las 00:00 UTC («2026-10-19T00:00:00+00:00», el inicio de un ciclo) es un
    // día y no un instante: `diaDeCalendario` lo reconoce y no se le inventa una hora.
    const soloDia = diaDeCalendario(texto) === dia && /^\d{4}-\d{2}-\d{2}[T ]00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)?$/.test(texto.trim());
    if (m[4] && !soloDia) {
        const instante = aFecha(texto);
        if (!instante) return null;
        dia = diaEnEspana(instante);
        hora = horaEnEspana(instante);
    }
    const [anio, mes, d] = dia.split('-').map(Number);
    if (mes < 1 || mes > 12 || d < 1 || d > 31) return null;
    const semana = DIAS[new Date(Date.UTC(anio, mes - 1, d)).getUTCDay()];
    return `${semana} ${d} de ${MESES[mes - 1]}${hora ? ` a las ${hora}` : ''}`;
};

// Cómo se le dice a una persona cada campo que guarda el reloj. El que no esté aquí sale con
// su nombre sin guiones bajos: se enseña antes que esconderlo.
const CAMPOS = {
    pausa_hasta: 'En pausa hasta',
    pausa_pedida: 'Pausa de 4 semanas pedida',
    pausa_alarga: 'Semanas que se alarga su plan',
    pausa_ultima: 'Su última pausa',
    plan_inicio: 'Su plan empieza',
    plan_siguiente_inicio: 'Su plan renovado empieza',
    plan_semanas: 'Semanas de su plan',
    num_semanas: 'Semanas de su ciclo',
    empieza: 'Empieza la semana 1',
    ultimo_pago: 'Último pago',
    siguiente_pago: 'Siguiente pago',
    pagado: 'Ha pagado',
    pago_deshecho: 'Pago deshecho',
    cuestionario_inicial: 'Cuestionario inicial',
    macros_entregados: 'Macros entregados',
    rutina_entregada: 'Rutina entregada',
    entrega_aplazada: 'Entrega aplazada hasta que responda',
    ajuste_pedido: 'Ha pedido su ajuste de macros',
    ajuste_vence: 'Plazo para pedir su ajuste',
    con_quincenal: 'Lleva reporte quincenal',
    programa: 'Tipo de reloj',
    reloj: 'Reloj',
    ficha: 'Plan',
};
const nombreDelCampo = (campo) => CAMPOS[campo] || mayuscula(legible(campo));

// Las claves de un valor que es un objeto (la última pausa, el apunte de fuera de hora).
const CLAVES = {
    semanas: 'semanas', fin: 'acabó el', desde: 'empezó el', regla: 'regla', ciclo: 'ciclo', semana: 'semana',
    access_until: 'acceso hasta el', current_period_end: 'periodo pagado hasta el', cycle_start: 'ciclo desde el',
};

/**
 * Un valor del reloj, dicho para una persona. NUNCA devuelve «[object Object]»: una lista sale
 * con sus elementos separados por comas y un objeto, con sus datos uno a uno.
 */
export const valorLegible = (v) => {
    if (v === true) return 'sí';
    if (v === false) return 'no';
    if (vacio(v)) return '';
    if (Array.isArray(v)) return v.map(valorLegible).filter(Boolean).join(', ');
    if (typeof v === 'object') {
        // Una duración va con el número delante («4 semanas») y antes que lo demás; y lo que
        // empezó, antes que lo que acabó, unidos por «y»: «4 semanas, empezó el lunes 16 de
        // noviembre y acabó el lunes 14 de diciembre a las 23:00».
        const dicho = (k) => valorLegible(v[k]);
        const trozos = [];
        if (typeof v.semanas === 'number') trozos.push(`${v.semanas} ${v.semanas === 1 ? 'semana' : 'semanas'}`);
        const tramo = ['desde', 'fin'].filter((k) => !vacio(v[k]) && dicho(k) !== '').map((k) => `${CLAVES[k]} ${dicho(k)}`);
        if (tramo.length > 0) trozos.push(tramo.join(' y '));
        const yaDichas = ['desde', 'fin', ...(typeof v.semanas === 'number' ? ['semanas'] : [])];
        Object.keys(v).filter((k) => !yaDichas.includes(k) && !vacio(v[k]) && dicho(k) !== '')
            .forEach((k) => trozos.push(`${CLAVES[k] || legible(k)} ${dicho(k)}`));
        return trozos.join(', ');
    }
    if (typeof v === 'string' && ESTADOS[v]) return ESTADOS[v];
    return fechaDelReloj(v) || legible(v);
};

const estadoDelReporte = (v) => (vacio(v) ? 'Sin enviar' : ESTADOS[v] || mayuscula(legible(v).toLowerCase()));

const deAntesADespues = (nombre, antes, despues) => {
    if (valorLegible(despues) === '') return `${nombre}: sin valor`;
    if (valorLegible(antes) === '') return `${nombre}: ${valorLegible(despues)}`;
    return `${nombre}: ${valorLegible(antes)} → ${valorLegible(despues)}`;
};

/**
 * Un cambio SIN regla (fijar la semana a mano, un pago, lo que apunta la pasada al leer la
 * ficha), dicho entero: «Semana: 2 → 3», «Fuera de hora: regla R7, ciclo 1, semana 2 → nada».
 * Lo usan el panel del reloj y quien pinte el historial tal cual: aquí sí se dice «nada».
 */
export const cambioEnClaro = (cambio) => {
    const { campo, antes, despues } = cambio || {};
    if (!campo) return '';
    return `${nombreDelCampo(campo)}: ${valorLegible(antes) || 'nada'} → ${valorLegible(despues) || 'nada'}`;
};

/** Un efecto («enviar_correo: reporte_mensual») dicho en una frase. Nunca devuelve vacío
 *  mientras llegue algo: uno desconocido se enseña legible, no se esconde. */
export const fraseDelEfecto = (efecto) => {
    const texto = String(efecto ?? '').trim();
    if (!texto) return '';
    const corte = texto.indexOf(':');
    const tipo = (corte === -1 ? texto : texto.slice(0, corte)).trim();
    const valor = corte === -1 ? '' : texto.slice(corte + 1).trim();
    if (tipo === 'enviar_correo' && valor) return CORREOS[valor] || `Se envía el correo: ${legible(valor)}`;
    if (tipo === 'tarea_equipo' && valor) return `Tarea para el equipo: ${TAREAS[valor] || legible(valor)}`;
    const conocido = OTROS_EFECTOS[`${tipo}: ${valor}`];
    if (conocido) return conocido;
    return mayuscula(valor ? `${legible(tipo)}: ${legible(valor)}` : legible(tipo));
};

// Lo que el servidor apunta en `resultado` cuando el efecto se hizo y era un correo
// (backend/core/acciones_del_reloj.py y los estados de core/correo.py).
const CORREO_HECHO = {
    'correo enviado': 'enviado',
    'correo desviado': 'desviado al buzón de pruebas',
    'correo bloqueado': 'bloqueado, no salió',
    'correo sin_enviar': 'sin enviar',
};

// Sin los nombres de variables del servidor entre paréntesis: «(RELOJ_ENVIA_SOLO)».
const sinVariables = (texto) => texto.replace(/\s*\([A-Z][A-Z0-9_=]*\)/g, '').trim();

/**
 * Cómo acabó un efecto, dicho en claro. El servidor lo deja en la MISMA línea del historial:
 * `resultado_estado` (hecho, caducado, agotado, sin_confirmar, descartado; de paso, reservado
 * y fallido) y `resultado` (su frase). Devuelve '' si el efecto aún no tiene resultado.
 */
export const resultadoDelEfecto = (cambio) => {
    const c = cambio || {};
    const estado = String(c.resultado_estado ?? '').trim();
    const dicho = sinVariables(String(c.resultado ?? '').trim());
    if (!estado && !dicho) return '';
    const esCorreo = String(c.despues ?? '').trim().startsWith('enviar_correo');
    switch (estado) {
        case 'caducado':
            return esCorreo ? 'caducado, no se envió' : 'caducado, no se hizo';
        case 'agotado':
            return 'falló varias veces, no se insiste';
        case 'sin_confirmar':
            return esCorreo ? 'no consta que saliera, no se reenvía' : 'no consta que se hiciera';
        case 'descartado': {
            const motivo = dicho.replace(/^no se ejecuta:\s*/i, '');
            return motivo ? `descartado, ${motivo}` : 'descartado';
        }
        case 'fallido':
            return 'falló, se reintenta';
        case 'reservado':
            return 'en curso';
        default:
            break;
    }
    // Hecho (o un estado que esta tabla no conoce): manda la frase del servidor.
    if (CORREO_HECHO[dicho]) return CORREO_HECHO[dicho];
    if (/^correo sin enviar/i.test(dicho)) return 'sin enviar, este entorno no tiene servidor de correo';
    if (/^no enviado:\s*fuera de la lista de pruebas/i.test(dicho)) return 'no enviado, fuera de la lista de pruebas';
    if (/^no enviado:/i.test(dicho)) return `no enviado, ${dicho.replace(/^no enviado:\s*/i, '')}`;
    if (dicho) return legible(dicho);
    return estado === 'hecho' ? 'hecho' : legible(estado);
};

/** Lo que cambió en UN cambio, dicho para una persona. Devuelve '' si el campo es interno. */
export const fraseDelCambio = (cambio) => {
    const { campo, antes, despues } = cambio || {};
    if (!campo || INTERNOS_DE_UNA_REGLA.includes(campo)) return '';
    switch (campo) {
        case 'efecto': {
            const frase = fraseDelEfecto(despues);
            const resultado = frase ? resultadoDelEfecto(cambio) : '';
            if (!resultado) return frase;
            // Un correo: «...: enviado». Lo demás ya lleva dos puntos: va entre paréntesis.
            return String(despues ?? '').trim().startsWith('enviar_correo') ? `${frase}: ${resultado}` : `${frase} (${resultado})`;
        }
        case 'semana':
            return deAntesADespues('Semana', antes, despues);
        case 'ciclo':
            return deAntesADespues('Ciclo', antes, despues);
        case 'reporte_mensual':
            return `Reporte mensual: ${estadoDelReporte(despues)}`;
        case 'reporte_quincenal':
            return `Reporte quincenal: ${estadoDelReporte(despues)}`;
        case 'mensual_respondido':
            return despues ? 'Ha respondido el mensual' : 'Aún no ha respondido';
        case 'baja':
            return despues ? 'De baja' : 'Ya no está de baja';
        case 'stop':
            return despues ? 'Reloj parado' : 'Reloj en marcha';
        default:
            // Un campo que se queda vacío no es una línea («Plazo para pedir su ajuste: sin
            // valor»): lo que importa de esa regla ya lo dicen sus otros cambios.
            if (valorLegible(despues) === '') return '';
            // Y el que esta tabla no conoce se enseña, legible, antes que esconderlo.
            return deAntesADespues(nombreDelCampo(campo), antes, despues);
    }
};

const tituloDe = (regla, cambios) => {
    const suyo = cambios.find((c) => typeof c.motivo === 'string' && c.motivo.startsWith(`${regla} ·`));
    return (suyo || cambios.find((c) => c.motivo) || {}).motivo || regla;
};

/**
 * Junta los cambios del reloj en las líneas que se pintan.
 *
 * `cambios`: la lista tal como llega (cada uno con `cuando`, `campo`, `antes`, `despues`,
 * `motivo` y, si lo movió una regla del catálogo, `regla`; puede traer `nombre` y `origen`).
 * `ocultar`: campos que esa pantalla ya no enseñaba en los cambios sin regla.
 *
 * De quién es cada cambio se sabe por su `cliente_id` (o `client_id`); sin id, por el `nombre`.
 *
 * Devuelve, en el orden en que aparecen, grupos de dos clases:
 *   - con regla:  { regla, titulo, detalles: [frases], cuando, nombre, origen, cambios }
 *   - sin regla:  { regla: null, titulo: motivo, detalles: [], cambio, cuando, nombre, origen, cambios: [cambio] }
 *     (`cambio` es el original, para que la pantalla lo pinte como lo pintaba).
 *
 * Un cambio sin regla de un campo interno no se enseña, salvo que con eso ese instante se
 * quedara sin ninguna línea: entonces sale una, solo con su motivo, para no perder el rastro.
 */
export const agruparCambios = (cambios, { ocultar = [] } = {}) => {
    const grupos = [];
    const porClave = new Map();
    const escondidos = [];
    // De quién es: su id si lo trae (dos clientes pueden llamarse igual); el nombre, a falta de id.
    const deQuien = (c) => {
        const id = c.cliente_id ?? c.client_id;
        return (id !== null && id !== undefined && id !== '') ? `id:${id}` : `nombre:${c.nombre ?? ''}`;
    };
    const instanteDe = (c) => `${c.cuando ?? ''}|${deQuien(c)}`;

    (Array.isArray(cambios) ? cambios : []).forEach((c) => {
        if (!c || typeof c !== 'object') return;
        if (!c.regla) {
            if (ocultar.includes(c.campo)) return;
            if (INTERNOS.includes(c.campo)) { escondidos.push(c); return; }
            grupos.push({ regla: null, titulo: c.motivo || '', detalles: [], cambio: c, cuando: c.cuando,
                nombre: c.nombre, cliente_id: c.cliente_id, client_id: c.client_id, origen: c.origen, cambios: [c] });
            return;
        }
        const clave = `${instanteDe(c)}|${c.regla}|${c.origen ?? ''}`;
        let grupo = porClave.get(clave);
        if (!grupo) {
            grupo = { regla: c.regla, titulo: '', detalles: [], cuando: c.cuando, nombre: c.nombre,
                cliente_id: c.cliente_id, client_id: c.client_id, origen: c.origen, cambios: [] };
            porClave.set(clave, grupo);
            grupos.push(grupo);
        }
        grupo.cambios.push(c);
    });

    grupos.forEach((g) => {
        if (!g.regla) return;
        g.titulo = tituloDe(g.regla, g.cambios);
        g.cambios.forEach((c) => {
            const frase = fraseDelCambio(c);
            if (frase && !g.detalles.includes(frase)) g.detalles.push(frase);
        });
    });

    // Que ningún instante desaparezca entero por haber escondido sus campos internos.
    const conLinea = new Set(grupos.map(instanteDe));
    escondidos.forEach((c) => {
        if (conLinea.has(instanteDe(c))) return;
        conLinea.add(instanteDe(c));
        grupos.push({ regla: null, titulo: c.motivo || '', detalles: [], cambio: null, cuando: c.cuando,
            nombre: c.nombre, cliente_id: c.cliente_id, client_id: c.client_id, origen: c.origen, cambios: [c] });
    });
    return grupos;
};

/** Los detalles de un grupo en una sola frase, para pintarlos al lado del título. */
export const detallesEnLinea = (grupo) => ((grupo && grupo.detalles) || []).join(' · ');

// EL MENSUAL QUE SE PIERDE AL FIJAR LA SEMANA (12-10). Fijar otra semana borra el estado de
// los reportes de la semana vieja (core/reloj.py, `fijar_semana`). Si el cliente tenía el
// mensual pedido y sin responder, el historial apuntaba "Enviado → nada" y a él le
// desaparecía de Seguimiento, sin que la pantalla lo avisara antes. No cambia la regla:
// quien fija la semana lo sabe antes de pulsar.
const MENSUAL_SIN_RESPONDER = ['ENVIADO', 'ENVIADO RECORDATORIO DOMINGO', 'ENVIADO RECORDATORIO LUNES', 'NO ENVIADO'];

/** La frase del aviso antes de fijar `semanaNueva` a ese reloj, o null si no se pierde nada. */
export const avisoAlFijarLaSemana = (reloj, semanaNueva) => {
    const r = reloj || {};
    if (!Number.isInteger(semanaNueva) || r.semana === semanaNueva) return null;
    if (r.mensual_respondido || !MENSUAL_SIN_RESPONDER.includes(r.reporte_mensual)) return null;
    const estado = (ESTADOS[r.reporte_mensual] || '').toLowerCase();
    return `Tiene el reporte mensual sin responder (${estado}). Al pasarlo de la semana ${r.semana} a la ${semanaNueva} `
        + 'se le deja de pedir: le desaparece de su Seguimiento y no queda como rellenado.';
};

/** "Aplazó su reporte mensual: lo tiene hasta el lunes 19 de octubre a las 18:00", o null.
 *  La regla 12 deja "No enviado" igual a quien aplazó que a quien no contestó (12-10). */
export const fraseDelAplazamiento = (hasta) => {
    const cuando = fechaDelReloj(hasta);
    return cuando ? `Aplazó su reporte mensual: lo tiene hasta el ${cuando}` : null;
};
